import { AsyncPipe } from '@angular/common'
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	inject,
	input,
	output,
	Signal,
} from '@angular/core'
import { getDownloadURL, ref, Storage } from '@angular/fire/storage'
import { FormControl, FormGroup, NgControl, ReactiveFormsModule } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { ShoeCalapod, ShoeOrderItem } from 'domain/order.model'
import { defer, firstValueFrom, forkJoin, Observable, of, switchMap } from 'rxjs'
import { filter, map, take } from 'rxjs/operators'
import { CartService } from 'src/app/modules/dashboard/shop/services/cart.service'
import { ConfirmationDialog } from '../../dialog/confirmation.dialog'
import { ControlValueAccessor } from '../../modules/form/core/control-value-accessor'
import { FmxFormSubmitDirective } from '../../modules/form/submit/form-submit.directive'
import { FormGroupValue } from '../../modules/form/core/form-types'
import { AppUserCurrency } from '../../../../../domain/price-categories'
import { assertNonNullish } from '../../../../../commons/assertions'
import { toObservable } from '@angular/core/rxjs-interop'

@Component({
	selector: 'app-cart-order-item-form',
	template: `
		<div [formGroup]="control" class="flex-align-space-between-center flex-gap-0-grid">
			<div class="article-image-container flex-22">
				@if (screenshot$ | async; as screenshot) {
					<img [src]="screenshot" width="140px" class="mix-blend-multiply" />
				}
			</div>
			<div class="name-column">{{ control.value.shoeName || '' | translate }}</div>
			<div class="size-column">{{ control.value.shoeSize }}</div>
			<div class="calapod-column">{{ control.value.shoeCalapod }}</div>
			<div class="quantity-column">
				<div class="quantity-input flex-align-space-between-center flex-gap-0-grid">
					<button mat-button type="button" class="dialog-exit-button" (click)="decrementQuantity()">
						<mat-icon>remove</mat-icon>
					</button>

					{{ control.value.amount }}

					<button mat-button type="button" class="dialog-exit-button" (click)="incrementQuantity()">
						<mat-icon>add</mat-icon>
					</button>
				</div>
			</div>
			<div class="price-column">{{ control.value.buyerPrice }} {{ currency() }}</div>
			<div class="delete-button">
				<button mat-button type="button" class="dialog-exit-button" (click)="openConfirmDialog()">
					<mat-icon>cancel</mat-icon>
				</button>
			</div>
		</div>
		<hr />
	`,
	styleUrls: ['./shared-cart-shoe-order-item-form-styles.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ReactiveFormsModule, FmxFormSubmitDirective, MatButton, MatIcon, AsyncPipe, TranslatePipe],
})
export class CartOrderItemFormComponent extends ControlValueAccessor<ShoeOrderItem> {
	protected readonly controlDirective: NgControl
	private readonly storage = inject(Storage)
	private readonly cartService = inject<CartService>(CartService)
	private readonly dialog = inject<MatDialog>(MatDialog)
	private readonly cdf = inject<ChangeDetectorRef>(ChangeDetectorRef)
	private readonly translateService = inject<TranslateService>(TranslateService)

	image: Observable<string> = of('')
	objectRemove = output<string>()

	readonly currency = input.required<AppUserCurrency>()

	readonly control = new FormGroup<FormGroupValue<ShoeOrderItem>>({
		id: new FormControl<string | null>(null),
		ixariaProductId: new FormControl<string | null>(null),
		ixariaProductName: new FormControl<string | null>(null),
		shoeUid: new FormControl<string | null>(null),
		shoeName: new FormControl<string | null>(null),
		shoeDisplayName: new FormControl<string | null>(null),
		shoeSize: new FormControl<number | null>(null),
		shoeCalapod: new FormControl<ShoeCalapod | null>(null),
		description: new FormControl<string>(''),
		adminPrice: new FormControl<number | null>(null),
		buyerPrice: new FormControl<number | null>(null),
		amount: new FormControl<number>(1),
		configurationSummary: new FormControl<ShoeOrderItem['configurationSummary'] | null>(null),
		ixariaConfigurationSnapshot: new FormControl<ShoeOrderItem['ixariaConfigurationSnapshot'] | null>(null),
		screenshots: new FormControl<ShoeOrderItem['screenshots'] | null>(null),
	})

	readonly itemId: Signal<string | null> = computed(() => this.controlValue()?.id ?? null)

	private readonly screenshotStoragePath: Signal<string | undefined> = computed(
		() => this.controlValue()?.screenshots?.position2StoragePath,
	)
	readonly screenshot$: Observable<string | undefined> = toObservable(this.screenshotStoragePath).pipe(
		switchMap((storagePath) => (storagePath ? getDownloadURL(ref(this.storage, storagePath)) : of(undefined))),
	)

	constructor() {
		const controlDirective = inject<NgControl>(NgControl)
		super(controlDirective)
		this.controlDirective = controlDirective

		const cartOrderId$ = this.cartService.currentOrder$.pipe(
			map((order) => order?.uid),
			filter(Boolean),
			take(1),
		)
		const shoeOrderItemId$: Observable<string> = toObservable(this.itemId).pipe(filter(Boolean), take(1))
		forkJoin([cartOrderId$, shoeOrderItemId$]).subscribe(([orderId, shoeId]) => {
			this.image = defer(() => getDownloadURL(ref(this.storage, `uploads/snapshots/${orderId}${shoeId}1.png`)))
		})
	}

	async openConfirmDialog() {
		const shoeDisplayName = this.control.value.shoeDisplayName
		assertNonNullish(shoeDisplayName, 'shoeDisplayName is null or undefined')

		const shoeName = await firstValueFrom(this.translateService.get(shoeDisplayName))

		const [title, question] = await firstValueFrom(
			forkJoin([
				this.translateService.get('dialog.shoopingCartRemove'),
				this.translateService.get('dialog.shoopingCartRemoveText', { name: shoeName }),
			]),
		)

		const dialogRef = this.dialog.open(ConfirmationDialog, {
			data: { title: title, question: question },
			width: '650px',
			autoFocus: false,
		})

		dialogRef.afterClosed().subscribe((remove) => {
			const itemId = this.itemId()
			assertNonNullish(itemId, 'Item id is null or undefined')

			if (remove) {
				this.objectRemove.emit(itemId)
				this.cartService.removeItem(itemId)
				this.cdf.markForCheck()
			}
		})
	}

	incrementQuantity(): void {
		const itemId = this.itemId()
		assertNonNullish(itemId, 'Item id is null or undefined')

		const newAmount = this.control.value.amount! + 1
		this.control.get('amount')?.setValue(newAmount)
		this.cartService.setItemAmount(itemId, newAmount)
	}

	decrementQuantity(): void {
		const itemId = this.itemId()
		assertNonNullish(itemId, 'Item id is null or undefined')

		const newAmount = this.control.value.amount! - 1
		if (newAmount) {
			this.control.get('amount')?.setValue(newAmount)
			this.cartService.setItemAmount(itemId, newAmount)
		} else {
			this.openConfirmDialog()
		}
	}
}
